import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PaymentMethodDisplayCard from "components/PaymentMethodDisplayCard";
import useDealerPaymentMethodSetup from "hooks/useDealerPaymentMethodSetup";
import styles from "./DealerCardSetupModal.module.css";

const steps = ["\u05e4\u05e8\u05d8\u05d9 \u05db\u05e8\u05d8\u05d9\u05e1", "\u05d0\u05d9\u05de\u05d5\u05ea", "\u05d0\u05d9\u05e9\u05d5\u05e8"];

function StepIndicator({ status }) {
  const activeIndex = status === "success" ? 2 : status === "processing" || status === "timeout" ? 1 : 0;

  return (
    <div className={styles.steps} aria-label={"\u05e9\u05dc\u05d1\u05d9 \u05e9\u05de\u05d9\u05e8\u05ea \u05db\u05e8\u05d8\u05d9\u05e1"}>
      {steps.map((label, index) => {
        const isDone = index < activeIndex || status === "success";
        const isActive = index === activeIndex;

        return (
          <div
            key={label}
            className={`${styles.step} ${isActive ? styles.stepActive : ""} ${isDone ? styles.stepDone : ""}`}
          >
            <span>{isDone ? "\u2713" : index + 1}</span>
            <strong>{label}</strong>
          </div>
        );
      })}
    </div>
  );
}

export default function DealerCardSetupModal({
  open,
  userId,
  mode = "profile",
  onClose,
  onCompleted,
  onContinue,
}) {
  const {
    loading,
    setup,
    status,
    error,
    createCardSetup,
    pollCardSetupStatus,
    reset,
    setStatus,
    setError,
  } = useDealerPaymentMethodSetup();
  const [completedPaymentMethod, setCompletedPaymentMethod] = useState(null);
  const startedRef = useRef(false);

  const beginPolling = useCallback(
    async (setupId) => {
      if (!setupId) return;

      try {
        const result = await pollCardSetupStatus(setupId);
        setCompletedPaymentMethod(result.payment_method);
        onCompleted?.(result.payment_method);
      } catch {
        // Error state is already reflected in the hook.
      }
    },
    [onCompleted, pollCardSetupStatus]
  );

  const startSetup = useCallback(async () => {
    if (!userId) {
      setStatus("error");
      setError("\u05de\u05e9\u05ea\u05de\u05e9 \u05dc\u05d0 \u05de\u05d6\u05d5\u05d4\u05d4. \u05e0\u05e1\u05d5 \u05dc\u05e8\u05e2\u05e0\u05df \u05d0\u05ea \u05d4\u05e2\u05de\u05d5\u05d3.");
      return;
    }

    setCompletedPaymentMethod(null);

    try {
      await createCardSetup(userId);
    } catch {
      // Error state is already reflected in the hook.
    }
  }, [createCardSetup, setError, setStatus, userId]);

  useEffect(() => {
    if (!open) {
      startedRef.current = false;
      setCompletedPaymentMethod(null);
      reset();
      return;
    }

    if (!startedRef.current) {
      startedRef.current = true;
      startSetup();
    }
  }, [open, reset, startSetup]);

  useEffect(() => {
    if (!open) return undefined;

    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;

      let message = event.data;
      if (typeof message === "string") {
        try {
          message = JSON.parse(message);
        } catch {
          return;
        }
      }

      if (message?.type === "payme_card_return") {
        beginPolling(message.setup_id || setup?.setup_id);
      }

      if (message?.type === "payme_card_cancel") {
        setStatus("error");
        setError("\u05e9\u05de\u05d9\u05e8\u05ea \u05d4\u05db\u05e8\u05d8\u05d9\u05e1 \u05d1\u05d5\u05d8\u05dc\u05d4. \u05d4\u05d4\u05e6\u05e2\u05d4 \u05dc\u05d0 \u05e0\u05e9\u05dc\u05d7\u05d4.");
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [beginPolling, open, setError, setStatus, setup?.setup_id]);

  if (!open) return null;

  const portalTarget = typeof document !== "undefined" ? document.body : null;
  if (!portalTarget) return null;

  const isOffer = mode === "offer";
  const isCreating = loading || status === "creating";
  const showIframe = setup?.sale_url && status === "iframe";
  const isProcessing = status === "processing";
  const isSuccess = status === "success" && completedPaymentMethod;
  const showError = status === "error" || status === "timeout";

  return createPortal(
    <div className={styles.overlay} dir="rtl" role="dialog" aria-modal="true" aria-labelledby="dealer-card-setup-title">
      <div className={styles.modal}>
        <button type="button" className={styles.closeButton} onClick={onClose} aria-label={"\u05e1\u05d2\u05d5\u05e8"}>
          ✕
        </button>

        <header className={styles.header}>
          <h2 id="dealer-card-setup-title">{"\u05d0\u05d9\u05e9\u05d5\u05e8 \u05d0\u05de\u05e6\u05e2\u05d9 \u05ea\u05e9\u05dc\u05d5\u05dd"}</h2>
          <p>
            {"\u05db\u05d3\u05d9 \u05dc\u05d4\u05de\u05e9\u05d9\u05da \u05d9\u05e9 \u05dc\u05d0\u05e9\u05e8 \u05d0\u05de\u05e6\u05e2\u05d9 \u05ea\u05e9\u05dc\u05d5\u05dd. \u05d4\u05db\u05e8\u05d8\u05d9\u05e1 \u05e0\u05e9\u05de\u05e8 \u05d1\u05e6\u05d5\u05e8\u05d4 \u05de\u05d0\u05d5\u05d1\u05d8\u05d7\u05ea \u05d1-PayMe."}
            {isOffer && " \u05dc\u05d0 \u05de\u05ea\u05d1\u05e6\u05e2 \u05d7\u05d9\u05d5\u05d1 \u05d1\u05e9\u05dc\u05d1 \u05d6\u05d4. \u05d4\u05d0\u05de\u05e6\u05e2\u05d9 \u05de\u05e9\u05de\u05e9 \u05dc\u05d0\u05d9\u05de\u05d5\u05ea \u05dc\u05e4\u05e0\u05d9 \u05d4\u05d2\u05e9\u05ea \u05d4\u05e6\u05e2\u05d4."}
          </p>
        </header>

        <StepIndicator status={status} />

        {isCreating && (
          <div className={styles.stateBox} aria-live="polite">
            <span className={styles.spinner} />
            <strong>{"\u05e4\u05d5\u05ea\u05d7\u05d9\u05dd \u05d8\u05d5\u05e4\u05e1 \u05de\u05d0\u05d5\u05d1\u05d8\u05d7..."}</strong>
            <p>{"\u05de\u05d9\u05d3 \u05d9\u05d5\u05e6\u05d2 \u05d8\u05d5\u05e4\u05e1 \u05e9\u05de\u05d9\u05e8\u05ea \u05d4\u05db\u05e8\u05d8\u05d9\u05e1 \u05e9\u05dc PayMe."}</p>
          </div>
        )}

        {showIframe && (
          <div className={styles.iframeBlock}>
            <span>{"\u05d8\u05d5\u05e4\u05e1 \u05de\u05d0\u05d5\u05d1\u05d8\u05d7 \u05e9\u05dc PayMe"}</span>
            <div className={styles.iframeShell}>
              <iframe src={setup.sale_url} title="PayMe card setup" allow="payment *" />
            </div>
          </div>
        )}

        {isProcessing && (
          <div className={styles.stateBox} aria-live="polite">
            <span className={styles.spinner} />
            <strong>{"\u05de\u05d0\u05de\u05ea\u05d9\u05dd \u05d0\u05ea \u05d4\u05e9\u05de\u05d9\u05e8\u05d4..."}</strong>
            <p>{"\u05d0\u05e0\u05d7\u05e0\u05d5 \u05de\u05d5\u05d5\u05d3\u05d0\u05d9\u05dd \u05e9\u05d4\u05db\u05e8\u05d8\u05d9\u05e1 \u05d0\u05d5\u05e9\u05e8 \u05d5\u05e0\u05e9\u05de\u05e8 \u05d1\u05de\u05e2\u05e8\u05db\u05ea."}</p>
          </div>
        )}

        {isSuccess && (
          <div className={`${styles.stateBox} ${styles.successBox}`} aria-live="polite">
            <span className={styles.checkIcon}>{"\u2713"}</span>
            <strong>{isOffer ? "\u05d0\u05de\u05e6\u05e2\u05d9 \u05d4\u05ea\u05e9\u05dc\u05d5\u05dd \u05d0\u05d5\u05e9\u05e8" : "\u05d0\u05de\u05e6\u05e2\u05d9 \u05d4\u05ea\u05e9\u05dc\u05d5\u05dd \u05e0\u05e9\u05de\u05e8 \u05d1\u05d4\u05e6\u05dc\u05d7\u05d4"}</strong>
            <PaymentMethodDisplayCard paymentMethod={completedPaymentMethod} variant="confirm" />
            <button type="button" className={styles.primaryButton} onClick={isOffer ? onContinue : onClose}>
              {isOffer ? "\u05d4\u05de\u05e9\u05da \u05dc\u05d4\u05d2\u05e9\u05ea \u05d4\u05d4\u05e6\u05e2\u05d4" : "\u05e1\u05d9\u05d5\u05dd"}
            </button>
          </div>
        )}

        {showError && (
          <div className={styles.errorBox} aria-live="assertive">
            <strong>{error || "\u05dc\u05d0 \u05d4\u05e6\u05dc\u05d7\u05e0\u05d5 \u05dc\u05d4\u05e9\u05dc\u05d9\u05dd \u05d0\u05ea \u05e9\u05de\u05d9\u05e8\u05ea \u05d4\u05db\u05e8\u05d8\u05d9\u05e1. \u05e0\u05e1\u05d5 \u05e9\u05d5\u05d1."}</strong>
            <div className={styles.actions}>
              <button type="button" className={styles.primaryButton} onClick={startSetup}>
                {"\u05e0\u05e1\u05d4 \u05e9\u05d5\u05d1"}
              </button>
              {setup?.setup_id && (
                <button type="button" className={styles.secondaryButton} onClick={() => beginPolling(setup.setup_id)}>
                  {"\u05d1\u05d3\u05d5\u05e7 \u05e9\u05d5\u05d1"}
                </button>
              )}
              <button type="button" className={styles.ghostButton} onClick={onClose}>
                {"\u05e1\u05d2\u05d5\u05e8"}
              </button>
            </div>
          </div>
        )}

        <p className={styles.footerNote}>{"\u05e4\u05e8\u05d8\u05d9 \u05d4\u05d0\u05e9\u05e8\u05d0\u05d9 \u05d0\u05d9\u05e0\u05dd \u05e0\u05e9\u05de\u05e8\u05d9\u05dd \u05d1\u05d0\u05ea\u05e8. \u05d4\u05e9\u05de\u05d9\u05e8\u05d4 \u05de\u05ea\u05d1\u05e6\u05e2\u05ea \u05d3\u05e8\u05da PayMe \u05d1\u05dc\u05d1\u05d3."}</p>
      </div>
    </div>
    ,
    portalTarget
  );
}
